import type { List } from 'immutable';
import { List as ImmutableList, Map as ImmutableMap, Record, type RecordOf } from 'immutable';
import { DateTime } from 'luxon';
import z from 'zod';

import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';

import { chartOfAccountsTypes } from '../chart-of-accounts';
import type PaymentMethodsType from '../fmcore-types/PaymentMethodsTypes';
import { tagsTypes } from '../tags';

/** Term snapshot stored on the invoice. Matches the Terms API response shape. */
export type Term = {
  id: string;
  name: string;
  days: number;
  clientId?: string;
  isDeleted?: boolean;
  isArchived?: boolean;
};

export const TRANSACTIONS_REDUCER_KEY = 'transactionStore';

export interface TransactionStoreProps extends QcsSyncResourceStoreProps<TransactionProps> {
  dateOnAfter?: string;
  dateOnBefore?: string;
  earliestDateOn?: string;
}

export type TransactionStore = QcsSyncResourceStore<TransactionProps, TransactionStoreProps>;
export const mkTransactionStore = Record<TransactionStoreProps>({
  resourcesById: ImmutableMap(),

  resourceName: 'TRANSACTIONS',
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,

  dateOnAfter: undefined,
  dateOnBefore: undefined,
  earliestDateOn: undefined,
});

export type LinkedItemProps = {
  id?: string | null;
  clientId?: string | null;
  childId?: string | null;
  childClientId?: string | null;
};
export type LinkedItem = RecordOf<LinkedItemProps>;
export const mkLinkedItem = Record<LinkedItemProps>({
  id: null,
  clientId: null,
  childId: null,
  childClientId: null,
});

export const SplitItemType = Object.freeze({
  NORMAL: 'NORMAL',

  INVOICE_BILLABLE_EXPENSE: 'INVOICE_BILLABLE_EXPENSE',
  INVOICE_BILLABLE_ITEM: 'INVOICE_BILLABLE_ITEM',
  INVOICE_BILLABLE_TIME: 'INVOICE_BILLABLE_TIME',
  INVOICE_BLANK_LINE: 'INVOICE_BLANK_LINE',
  INVOICE_BUNDLE: 'INVOICE_BUNDLE',
  INVOICE_CREDIT: 'INVOICE_CREDIT',
  INVOICE_DEPOSIT: 'INVOICE_DEPOSIT',
  INVOICE_DISCOUNT: 'INVOICE_DISCOUNT',
  INVOICE_ITEM_DISCOUNT: 'INVOICE_ITEM_DISCOUNT',
  INVOICE_ITEM_PRICE_RULE: 'INVOICE_ITEM_PRICE_RULE',
  INVOICE_ITEM_SUBTOTAL: 'INVOICE_ITEM_SUBTOTAL',
  INVOICE_ITEM_SURCHARGE: 'INVOICE_ITEM_SURCHARGE',
  INVOICE_ITEM_TAX: 'INVOICE_ITEM_TAX',
  INVOICE_SHIPPING: 'INVOICE_SHIPPING',
  INVOICE_SHIPPING_TAX: 'INVOICE_SHIPPING_TAX',
  INVOICE_SUBTOTAL: 'INVOICE_SUBTOTAL',
  INVOICE_TAX: 'INVOICE_TAX',
  INVOICE_TAX_PRE_DISCOUNT: 'INVOICE_TAX_PRE_DISCOUNT',
  INVOICE_TIP: 'INVOICE_TIP',

  LOAN_INTEREST: 'LOAN_INTEREST',
  LOAN_PRINCIPAL: 'LOAN_PRINCIPAL',
});

export type SplitItemProps = {
  id?: string | null;
  clientId?: string | null;
  parentItemId?: string | null;
  parentItemClientId?: string | null;
  type?: (typeof SplitItemType)[keyof typeof SplitItemType] | null;
  serviceOn?: string | null;
  itemId?: string | null;
  name?: string | null;
  memo?: string | null;
  isOverflow?: boolean | null;
  tags?: List<tagsTypes.Tag> | Array<tagsTypes.TagProps> | null;
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null;
  amount: number;
  units?: number | null;
  rate?: number | null;
  isRatePercentage?: boolean | null;
  businessId?: string | null;
  customerId?: string | null;
  projectId?: string | null;
  isBillable?: boolean | null;
  isTaxable?: boolean | null;
  transfer?: { clientId?: string | null; id?: string | null } | null;
  linkedItem?: LinkedItem | LinkedItemProps | null;
  isExcludedFromF2S?: boolean | null;
  isExcludedFromReports?: boolean | null;
};

export type SplitProps = {
  doPatch?: boolean | null;
  items?: List<RecordOf<SplitItemProps>> | Array<SplitItemProps> | null;
};

export const BaseSplitItem = ({ tags, coa, ...defaultValues }: Partial<SplitItemProps>) =>
  class extends Record<SplitItemProps>({
    id: defaultValues.id || null,
    clientId: null,

    parentItemId: undefined,
    parentItemClientId: undefined,

    type: SplitItemType.NORMAL,

    serviceOn: undefined,
    itemId: undefined,
    name: undefined,
    memo: undefined,
    isOverflow: false,

    coa: chartOfAccountsTypes.mkChartOfAccount({
      id: '0',
      type: 'UNCATEGORIZED',
    }),
    amount: 0.0,

    units: undefined,
    rate: undefined,
    isRatePercentage: false,

    businessId: undefined,
    customerId: undefined,
    projectId: undefined,
    tags: ImmutableList([]),

    isBillable: false,
    isTaxable: false,

    transfer: undefined,
    linkedItem: undefined,

    // These cannot be set by default, cause on QCS, txn should override unless user specifically sets. Currently,
    // client doesn't yet support setting these at the split-level in the UI.
    isExcludedFromF2S: undefined,
    isExcludedFromReports: undefined,

    ...defaultValues,
  }) {};

export const BaseSplit = (defaultValues: Partial<SplitProps>) =>
  class extends Record<SplitProps>({
    doPatch: undefined,
    items: null,

    ...defaultValues,
  }) {};

export class Split extends BaseSplit({}) {}

export class SplitItem extends BaseSplitItem({}) {}

export const mkSplit = (split: SplitProps | { doPatch?: boolean; items: SplitItemProps[] }) =>
  new Split({
    doPatch: split.doPatch,
    items: mkSplitItems(split.items || ImmutableList()),
  });

export const mkSplitItem = (item: Partial<SplitItemProps> | RecordOf<SplitItemProps>) =>
  new SplitItem({
    ...(('toJS' in item ? item.toJS() : item) as SplitItemProps),
    tags: item.tags ? ImmutableList(item.tags.map((tag) => tagsTypes.mkTag(tag))) : undefined,
    coa: item.coa ? chartOfAccountsTypes.mkChartOfAccount({ id: item.coa.id, type: item.coa.type }) : null,
    linkedItem: item.linkedItem ? mkLinkedItem(mkLinkedItem(item.linkedItem)) : undefined,
  });

export function mkSplitItems(items: List<RecordOf<SplitItemProps>> | Array<SplitItemProps>) {
  return ImmutableList(items.map((item) => mkSplitItem(item)));
}

export type AllocationProps = {
  txnId: string;
  amount: number;
  type: 'PAYMENT' | 'TIP' | 'CREDIT';
};

export const BaseAllocation = (defaultValues?: Partial<AllocationProps> | null) =>
  class extends Record<AllocationProps>({
    txnId: undefined!,
    amount: undefined!,
    type: undefined!,
    ...defaultValues,
  }) {};
export class Allocation extends BaseAllocation({}) {}
export const mkAllocation = (allocation: Partial<AllocationProps> = {}) => new Allocation(allocation);

export type Credit = {
  txn: TransactionRecord;
  amount: number;
};

export type QcsColor = 'RED' | 'ORANGE' | 'YELLOW' | 'GREEN' | 'BLUE' | 'VIOLET' | 'BLACK' | 'BROWN' | 'NONE';

export enum TransactionSource {
  UserEntered = 'USER_ENTERED',
  QcsRefresh = 'QCS_REFRESH',
  QcsSync = 'QCS_SYNC',
  QcsGenerated = 'QCS_GENERATED',
  QcsBatchRefresh = 'QCS_BATCH_REFRESH',
  ClientAggregated = 'CLIENT_AGGREGATED',
  ClientAggregatedBankPending = 'CLIENT_AGGREGATED_BANK_PENDING',
  QcsRefreshBankPending = 'QCS_REFRESH_BANK_PENDING',
  QcsBatchRefreshBankPending = 'QCS_BATCH_REFRESH_BANK_PENDING',
  RealTime = 'REAL_TIME',
  UserOwnedBankPending = 'USER_OWNED_BANK_PENDING',
  UserOwnedRealTimeBankPending = 'USER_OWNED_REAL_TIME_BANK_PENDING',
  ScheduledTransactionPending = 'SCHEDULED_TRANSACTION_PENDING',
  ScheduledTransactionUnkown = 'SCHEDULED_TRANSACTION_UNKNOWN',
  ScheduledTransaction = 'SCHEDULED_TRANSACTION',
  ScheduledTransactionSkipped = 'SCHEDULED_TRANSACTION_SKIPPED',
  Unknown = 'UNKNOWN',
}

export enum TransactionSubType {
  A2aTransfer = 'A2A_TRANSFER',
  A2aTransferShadowTxn = 'A2A_SHADOW_TXN',

  GoalStartingAmount = 'GOAL_STARTING_AMOUNT',
  GoalSpent = 'GOAL_SPENT',

  // Start business subtypes
  Estimate = 'ESTIMATE',
  InvoiceCreditMemo = 'INVOICE_CREDIT_MEMO',
  InvoicePayment = 'INVOICE_PAYMENT',
  Invoice = 'INVOICE',
  SalesReceipt = 'SALES_RECEIPT',
  SalesRefund = 'SALES_REFUND',
  // End business subtypes

  // These are not used for business, only used for personal transaction refunds.
  Refund = 'REFUND',
  RefundShadowTxn = 'REFUND_SHADOW_TXN',
}

export type TransactionState = 'CLEARED' | 'PENDING' | 'RECONCILED';

export type TransactionType = 'INVESTMENT' | 'CASH_FLOW' | 'INVOICE' | 'PAYMENT';

export type CpData = {
  amount?: number;
  cpCategoryId?: string;
  id?: string;
  inferredCoa?: chartOfAccountsTypes.ChartOfAccount | chartOfAccountsTypes.ChartOfAccountProps;
  payee?: string;
  inferredPayee?: string;
  memo?: string;
  postedOn?: string;
  txOn?: string;
  cpPendingId?: string;

  /** The aggregators internal id for payment type txns such as those received from Stripe. */
  cpPaymentIntentId?: string;
};

export enum AttachmentSource {
  Storage = 'STORAGE',
  Receipt = 'RECEIPT',
}

export type Attachment = {
  id: string;
  /** Missing on legacy transaction resources; qcs-transactions treats those attachments as STORAGE. */
  source?: `${AttachmentSource}` | null;
};

export const isDocumentAttachment = (attachment: Attachment): boolean =>
  attachment.source == null || attachment.source === AttachmentSource.Storage;

export const isReceiptAttachment = (attachment: Attachment): boolean => attachment.source === AttachmentSource.Receipt;

export const getDocumentAttachments = (attachments: Attachment[] | null | undefined): Attachment[] =>
  attachments?.filter(isDocumentAttachment) ?? [];

export const getReceiptAttachments = (attachments: Attachment[] | null | undefined): Attachment[] =>
  attachments?.filter(isReceiptAttachment) ?? [];

/** Product limit for each attachment source per transaction (server + all clients). */
export const MAX_ATTACHMENTS_PER_TRANSACTION = 10;

export function capTxnAttachmentsList(attachments: Attachment[] | null | undefined): Attachment[] | null | undefined {
  if (!attachments?.length) return attachments ?? undefined;

  let documentCount = 0;
  let receiptCount = 0;

  return attachments.filter((attachment) => {
    if (isReceiptAttachment(attachment)) {
      receiptCount += 1;
      return receiptCount <= MAX_ATTACHMENTS_PER_TRANSACTION;
    }

    documentCount += 1;
    return documentCount <= MAX_ATTACHMENTS_PER_TRANSACTION;
  });
}

export type EstimateStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CONVERTED' | 'EXPIRED';

export const TxnPaymentMethod = z.enum(['CASH', 'CHECK', 'CREDIT_CARD', 'DEBIT_CARD', 'ONLINE_PAYMENT', 'OTHER']);
export type TxnPaymentMethodType = z.infer<typeof TxnPaymentMethod>;

export type TransactionProps = {
  id?: string | null;
  clientId?: string | null;
  memo?: string | null;
  amount: number;
  tags?: List<tagsTypes.Tag> | Array<tagsTypes.TagProps> | null;
  accountId?: string | null;
  postedOn: string;
  isBillable?: boolean | null;
  isExcludedFromReports?: boolean | null;
  isExcludedFromF2S?: boolean | null;
  userFlag?: QcsColor | null;
  userFlagNote?: string | null;
  payee?: string | null;
  knownCategoryId?: string | null;
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null;
  split?: RecordOf<SplitProps> | SplitProps | null;
  attachments?: Attachment[] | null;
  isReviewed?: boolean | null;
  state?: `${TransactionState}`;
  source?: `${TransactionSource}`;
  stModelId?: string | null;
  stDueOn?: string | null;
  paymentProvider?: string | null;
  paymentSentStatus?: string | null;
  paymentId?: string | null;
  createdAt?: string | null;
  allocations?: List<Allocation> | Array<AllocationProps> | null;
  modifiedAt?: string | null;
  isBill?: boolean | null;
  ebillId?: string | null;
  isSubscription?: boolean;
  subtype?: TransactionSubType | null;
  subtypeRelatedTxn?: { id?: string } | null; // deprecated - use linkedItem instead
  linkedItem?: LinkedItem | LinkedItemProps | null;
  type?: TransactionType | null;
  isDeleted?: boolean | null;
  isTransfer?: boolean | null;
  transfer?: { clientId?: string | null; id?: string | null } | null;
  matchedTxn?: { id?: string } | null;
  investmentTxnType?: (typeof InvestmentTxnType)[keyof typeof InvestmentTxnType] | '' | null;
  units?: number | null;
  cpData?: CpData | null;
  localSecurityId?: string | null;
  securityId?: string | null; // deprecated - use localSecurityId instead
  securityIdType?: string | null; // deprecated - use localSecurityId instead
  securityType?: string | null; // deprecated - use localSecurityId instead
  symbol?: string | null;
  securityName?: string | null;
  costBasis?: number | null;
  commission?: number | null;
  splitNumerator?: number | null;
  splitDenominator?: number | null;
  // FIXME: This prop may not exist
  acquisitionOn?: string | null;
  // FIXME: This prop may not exist
  currency?: string | null;
  businessId?: string | null;
  matchState?: string | null;
  customerId?: string | null;
  projectId?: string | null;
  // FIXME: the balance field may not exist?
  balance?: number | null;
  check?: {
    number?: string | null;
    memo?: string | null;
  } | null;
  // FIXME: is coordinates needed? what does the field look like?
  coordinates?: any | null;
  invoiceNo?: string | null;
  toEmailAddresses?: string[] | null;
  ccEmailAddresses?: string[] | null;
  bccEmailAddresses?: string[] | null;

  sentOn?: string | null;

  billingAddress?: string | null;
  termsId?: string | null;
  terms?: Term | null;
  dueOn?: string | null;
  invoiceDisplayOptions?: { showInvoiceTerms?: boolean } | null;

  locOfSale?: string | null;

  shippingAddress?: string | null;
  shippingVia?: string | null;
  shippingOn?: string | null;
  shippingTrackingNo?: string | null;

  msgOnInvoice?: string | null;
  msgOnStatement?: string | null;

  emailSubject?: string | null;
  emailMessage?: string | null;

  /** Payment Methods for integrated Payment Providers (Stripe) */
  paymentMethods?: List<PaymentMethodsType> | Array<PaymentMethodsType> | null;
  /** User entered payment method string (for something like a receipt) */
  paymentMethod?: TxnPaymentMethodType | null;
  /** Alpha-numeric user entered reference id */
  referenceNo?: string | null;
  includeBilledItemAttachments?: boolean | null;
  includeInvoiceAttachments?: boolean | null;

  // fields for PaymentTransaction
  paymentTxnType?: (typeof PaymentTxnType)[keyof typeof PaymentTxnType] | 'UNKNOWN' | null;

  renamedPayee?: string;
  transactionRuleId?: string;
  estimateStatus?: EstimateStatus | null;
  acceptedOn?: string | null;
  acceptedBy?: string | null;
  expireOn?: string | null;

  logoId?: string | null;
  logoUrl?: string | null;
};

export type TransactionRecord = RecordOf<TransactionProps>;

export const Transaction = (defaultValues: Partial<TransactionProps>) =>
  class extends Record<TransactionProps>({
    id: null,

    accountId: undefined,

    clientId: undefined, // this is a ONE TIME instantiation, don't do this.  uuidv4().toUpperCase(),
    postedOn: DateTime.local().toISODate()!,

    payee: null,
    memo: '',
    knownCategoryId: undefined,
    coa: chartOfAccountsTypes.mkChartOfAccount({
      id: '0',
      type: 'UNCATEGORIZED',
    }),
    amount: 0.0,
    split: null,

    businessId: undefined,
    customerId: undefined,
    projectId: undefined,

    tags: null,
    attachments: null,

    state: 'PENDING',
    matchState: 'NOT_MATCHED', // NOT_MATCHED, AUTO_MATCHED, USER_MATCHED, USER_UNMATCHED
    isReviewed: false,
    transfer: null,

    cpData: null,

    stModelId: null,
    stDueOn: null,
    paymentProvider: null,

    paymentSentStatus: null,
    paymentId: null,

    ebillId: null,

    source: TransactionSource.UserEntered,
    isDeleted: false,
    modifiedAt: undefined,

    isExcludedFromReports: false,
    isExcludedFromF2S: false,

    userFlag: null,
    userFlagNote: null,

    matchedTxn: null,

    isBill: false,
    isSubscription: false,

    isBillable: false,

    subtype: undefined,
    subtypeRelatedTxn: undefined,
    linkedItem: undefined,
    createdAt: undefined,

    allocations: undefined,
    // fields specific to web app (unknown to QCS)
    //
    balance: undefined,

    renamedPayee: undefined,
    transactionRuleId: undefined,
    estimateStatus: undefined,
    acceptedOn: undefined,
    acceptedBy: undefined,
    expireOn: undefined,

    logoId: undefined,
    logoUrl: undefined,

    ...defaultValues,
  }) {};

export const TransactionPatch = (defaultValues: Partial<TransactionProps>) =>
  class extends Record<TransactionProps>({
    id: undefined,

    accountId: undefined,

    clientId: undefined,
    postedOn: undefined!,

    payee: undefined,
    memo: undefined,
    coa: undefined,
    amount: undefined!,
    split: undefined,

    businessId: undefined,
    customerId: undefined,
    projectId: undefined,

    tags: undefined,
    attachments: undefined,

    state: undefined,
    matchState: undefined,
    isReviewed: undefined,
    transfer: undefined,

    cpData: undefined,

    stModelId: undefined,
    stDueOn: undefined,
    paymentProvider: undefined,

    paymentSentStatus: undefined,
    paymentId: undefined,

    ebillId: undefined,

    source: undefined,
    isDeleted: undefined,
    modifiedAt: undefined,

    isExcludedFromReports: undefined,
    isExcludedFromF2S: undefined,

    userFlag: undefined,
    userFlagNote: undefined,

    matchedTxn: undefined,

    isBill: undefined,
    isSubscription: undefined,

    isBillable: undefined,

    subtype: undefined,
    subtypeRelatedTxn: undefined,
    linkedItem: undefined,
    createdAt: undefined,

    allocations: undefined,

    renamedPayee: undefined,
    transactionRuleId: undefined,

    logoId: undefined,
    logoUrl: undefined,

    // fields specific to web app (unknown to QCS)
    //
    balance: undefined,

    ...defaultValues,
  }) {};

// used to ensure tags, splits, and coa are saved as immutable records.
// a patch leaves an absent coa `undefined` so it means "leave the stored one alone" on merge;
// a full record floors to `null`, since undefined falls through to the UNCATEGORIZED default
function baseTxnMap(
  txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {},
  mode: 'record' | 'patch' = 'record',
) {
  const plain = ('toJS' in txn ? txn.toJS() : txn) as Partial<TransactionProps>;
  return {
    ...plain,
    memo: txn.memo || undefined,
    coa: txn.coa
      ? chartOfAccountsTypes.mkChartOfAccount({ id: txn.coa.id, type: txn.coa.type })
      : mode === 'patch' && txn.coa === undefined
        ? undefined
        : null,
    tags: txn.tags ? ImmutableList(txn.tags.map((tag) => tagsTypes.mkTag(tag))) : undefined,
    split: txn.split ? mkSplit(txn.split) : undefined,
    allocations: txn.allocations
      ? ImmutableList(txn.allocations.map((allocation) => mkAllocation(allocation)))
      : undefined,
    linkedItem: txn.linkedItem ? mkLinkedItem(txn.linkedItem) : undefined,
    attachments: capTxnAttachmentsList(plain.attachments),
  } as Partial<TransactionProps>;
}

export class CashFlowTransaction extends Transaction({
  type: 'CASH_FLOW',
  check: {
    number: '',
    memo: '',
  },
  coordinates: null,
}) {}

export const mkCashFlowTransaction = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new CashFlowTransaction({
    ...baseTxnMap(txn),
    check: txn.check,
    coordinates: txn.coordinates,
  });

export class CashFlowTransactionPatch extends TransactionPatch({
  type: 'CASH_FLOW',
  check: undefined,
  coordinates: undefined,
}) {}

export const mkCashFlowTransactionPatch = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new CashFlowTransactionPatch({
    ...baseTxnMap(txn, 'patch'),
    check: txn.check,
    coordinates: txn.coordinates,
  });

export class InvoiceTransaction extends Transaction({
  type: 'INVOICE',

  invoiceNo: undefined,

  toEmailAddresses: undefined,
  ccEmailAddresses: undefined,
  bccEmailAddresses: undefined,

  sentOn: undefined,

  billingAddress: undefined,
  termsId: undefined,
  terms: undefined,
  dueOn: undefined,
  invoiceDisplayOptions: undefined,

  locOfSale: undefined,

  shippingAddress: undefined,
  shippingVia: undefined,
  shippingOn: undefined,
  shippingTrackingNo: undefined,

  msgOnInvoice: undefined,
  msgOnStatement: undefined,

  emailSubject: undefined,
  emailMessage: undefined,

  paymentMethods: undefined,
  includeBilledItemAttachments: undefined,
  includeInvoiceAttachments: undefined,

  paymentMethod: undefined,
  referenceNo: undefined,
}) {}

export const mkInvoiceTransaction = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new InvoiceTransaction({
    ...baseTxnMap(txn),

    invoiceNo: txn.invoiceNo,

    toEmailAddresses: txn.toEmailAddresses,
    ccEmailAddresses: txn.ccEmailAddresses,
    bccEmailAddresses: txn.bccEmailAddresses,

    sentOn: txn.sentOn,

    billingAddress: txn.billingAddress,
    termsId: txn.termsId,
    terms: txn.terms,
    dueOn: txn.dueOn,
    invoiceDisplayOptions: txn.invoiceDisplayOptions,

    locOfSale: txn.locOfSale,

    shippingAddress: txn.shippingAddress,
    shippingVia: txn.shippingVia,
    shippingOn: txn.shippingOn,
    shippingTrackingNo: txn.shippingTrackingNo,

    msgOnInvoice: txn.msgOnInvoice,
    msgOnStatement: txn.msgOnStatement,

    emailSubject: txn.emailSubject,
    emailMessage: txn.emailMessage,

    paymentMethods: txn.paymentMethods ? ImmutableList(txn.paymentMethods) : undefined,
    includeInvoiceAttachments: txn.includeInvoiceAttachments,
    includeBilledItemAttachments: txn.includeBilledItemAttachments,
    estimateStatus: txn.estimateStatus,
    acceptedOn: txn.acceptedOn,
    acceptedBy: txn.acceptedBy,
    expireOn: txn.expireOn,

    paymentMethod: txn.paymentMethod,
    referenceNo: txn.referenceNo,
  });

export const InvestmentTxnType = Object.freeze({
  ADD_SHARES: 'ADD_SHARES', // aggregated
  BUY: 'BUY', // aggregated
  BUY_BONDS: 'BUY_BONDS',
  COVER_SHORT_SELL: 'COVER_SHORT_SELL',
  EXPENSE_MARGIN_INTEREST: 'EXPENSE_MARGIN_INTEREST',
  EXPENSE_MISCELLANEOUS: 'EXPENSE_MISCELLANEOUS', // aggregated
  CAPITAL_GAIN_LONG: 'CAPITAL_GAIN_LONG',
  CAPITAL_GAIN_SHORT: 'CAPITAL_GAIN_SHORT',
  INCOME_DIVIDEND: 'INCOME_DIVIDEND', // aggregated
  INCOME_INTEREST: 'INCOME_INTEREST', // aggregated
  INCOME_MISCELLANEOUS: 'INCOME_MISCELLANEOUS', // aggregated
  REINVEST_CAPITAL_GAIN_LONG: 'REINVEST_CAPITAL_GAIN_LONG',
  REINVEST_CAPITAL_GAIN_SHORT: 'REINVEST_CAPITAL_GAIN_SHORT',
  REINVEST_DIVIDEND: 'REINVEST_DIVIDEND', // aggregated
  REINVEST_INTEREST: 'REINVEST_INTEREST',
  REMOVE_SHARES: 'REMOVE_SHARES', // aggregated
  RETURN_OF_CAPITAL: 'RETURN_OF_CAPITAL',
  SELL: 'SELL', // aggregated
  SELL_BONDS: 'SELL_BONDS',
  SHORT_SELL: 'SHORT_SELL', // aggregated
  STOCK_DIVIDEND: 'STOCK_DIVIDEND',
  STOCK_SPLIT: 'STOCK_SPLIT',
  UNKNOWN: 'UNKNOWN',
});

export class InvestmentTransaction extends Transaction({
  type: 'INVESTMENT',
  investmentTxnType: '', // InvestmentTxnType
  units: 0,
  symbol: '', // deprecated - use localSecurityId instead
  securityName: '', // deprecated - use localSecurityId instead
  securityId: undefined, // deprecated - use localSecurityId instead
  securityIdType: undefined, // deprecated - use localSecurityId instead
  securityType: undefined, // deprecated - use localSecurityId instead
  localSecurityId: undefined,
  costBasis: 0,
  commission: 0,
  splitNumerator: undefined,
  splitDenominator: undefined,

  // props below are not confirmed
  acquisitionOn: undefined,
  currency: undefined,
}) {}

export const mkInvestmentTransaction = (txn = {}) =>
  new InvestmentTransaction({
    ...baseTxnMap(txn),
  });

export const PaymentTxnType = Object.freeze({
  CHARGE: 'CHARGE',
  CHARGE_FAILURE: '',
  DISPUTE: 'DISPUTE',
  DISPUTE_REVERSAL: '',
  PARTIAL_CAPTURE_REVERSAL: 'PARTIAL_CAPTURE_REVERSAL',
  REFUND: 'REFUND',
  REFUND_FAILURE: '',
  ANTICIPATION_REPAYMENT: 'ANTICIPATION_REPAYMENT',
  CLIMATE_ORDER_PURCHASE: 'CLIMATE_ORDER_PURCHASE',
  CLIMATE_ORDER_REFUND: 'CLIMATE_ORDER_REFUND',
  CONTRIBUTION: 'CONTRIBUTION',
  FEE: 'FEE',
  OTHER_ADJUSTMENT: 'OTHER_ADJUSTMENT',
  PAYOUT: 'PAYOUT',
  PAYOUT_REVERSAL: 'PAYOUT_REVERSAL',
  PLATFORM_EARNING: 'PLATFORM_EARNING',
  PLATFORM_EARNING_REFUND: 'PLATFORM_EARNING_REFUND',
  TRANSFER: 'TRANSFER',
  TRANSFER_REVERSAL: 'TRANSFER_REVERSAL',
  TAX: 'TAX',
  TOPUP: 'TOPUP',
  TOPUP_REVERSAL: 'TOPUP_REVERSAL',
  PAYMENT_NETWORK_RESERVE_HOLD: 'PAYMENT_NETWORK_RESERVE_HOLD',
  PAYMENT_NETWORK_RESERVE_RELEASE: 'PAYMENT_NETWORK_RESERVE_RELEASE',
  UNKNOWN: 'UNKNOWN',
});

export class PaymentTransaction extends Transaction({
  type: 'PAYMENT',
  paymentTxnType: 'UNKNOWN',
}) {}

export const mkPaymentTransaction = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new PaymentTransaction({
    ...baseTxnMap(txn),

    paymentTxnType: txn.paymentTxnType,
  });

export const statusOrder = ['RECONCILED', 'CLEARED', 'PENDING'];

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [TRANSACTIONS_REDUCER_KEY]: TransactionStore;
  }
}

export interface TransactionItemCombinedProps extends Omit<TransactionProps, 'type'>, Omit<SplitItemProps, 'type'> {
  type?: TransactionType | (typeof SplitItemType)[keyof typeof SplitItemType] | null;
  unpaidAmount?: number;
  isPartial?: boolean;
  readOnly?: boolean;
  paidAmount?: number;
  paymentTxnId?: string;
}

export type TransactionItemProps = {
  transaction: RecordOf<TransactionProps>; // parent transction
  splitItem?: RecordOf<SplitItemProps>; // optional split item
  item: TransactionItemCombinedProps; // for convenience
};
export type TransactionItem = RecordOf<TransactionItemProps>;
const TransactionItemRecord = Record<TransactionItemProps>({
  transaction: undefined!, // Using `!` to bypass undefined, will be set by factory
  splitItem: undefined,
  item: undefined!, // Using `!` to bypass undefined, will be set by factory
});
export const mkTransactionItem = (props: TransactionItemProps): RecordOf<TransactionItemProps> =>
  TransactionItemRecord(props);

// export const mkTransactionItem = (props: {
//   transaction: RecordOf<TransactionProps>;
//   splitItem?: RecordOf<SplitItemProps>;
//   item: TransactionItemCombinedProps;
// }): TransactionItem => {
//   return TransactionItemRecord(props) as TransactionItem;
// };

export type A2ATransferPayload = {
  srcAccountId: string;
  dstAccountId: string;
  amount: string;
  initiateOn?: string; // if omited defaults to current day I think
};

export enum StatusChipTypes {
  TODAY = 'TODAY',
  UPCOMING = 'UPCOMING',
  PAST = 'PAST',
  PAID = 'PAID',
  RECEIVED = 'RECEIVED',
  SENT = 'SENT',
  SUCCESS = 'SUCCESS',
}

export type CreditTxnProps = { businessId?: string | null; customerId?: string | null; projectId?: string | null };

export type AvailableCreditsProps = { action?: string; srcTxn?: TransactionRecord } & CreditTxnProps;

export type UpdatedAllocationsProps = {
  creditedAmount: number;
  allocations?: TransactionRecord['allocations'];
} & CreditTxnProps;
